import type { QueryClient } from '@tanstack/react-query';
import type { WhatsAppInboxChat } from '@zrsystem/contracts';

import { notificationQueryKeys } from '@/features/notification/query-keys.js';

interface WhatsAppInboxChatsCache {
  readonly chats: WhatsAppInboxChat[];
  readonly accountUnavailable: boolean;
}

interface WhatsAppInboxUnreadState {
  apiUnread: Record<string, number>;
  bumps: Record<string, number>;
  seenAt: Record<string, number>;
}

const emptyState = (): WhatsAppInboxUnreadState => ({
  apiUnread: {},
  bumps: {},
  seenAt: {},
});

function storageKey(placeId: string): string {
  return `zrsystem.whatsapp-inbox-unread.${placeId}`;
}

function readState(placeId: string): WhatsAppInboxUnreadState {
  if (typeof sessionStorage === 'undefined') {
    return emptyState();
  }
  const raw = sessionStorage.getItem(storageKey(placeId));
  if (raw === null) {
    return emptyState();
  }
  try {
    const parsed = JSON.parse(raw) as Partial<WhatsAppInboxUnreadState>;
    return {
      apiUnread: parsed.apiUnread ?? {},
      bumps: parsed.bumps ?? {},
      seenAt: parsed.seenAt ?? {},
    };
  } catch {
    return emptyState();
  }
}

function writeState(placeId: string, state: WhatsAppInboxUnreadState): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(storageKey(placeId), JSON.stringify(state));
}

/**
 * GOWS overview omits unreadCount (always 0). Keep a local count so the badge
 * survives a list refresh, and seed 1 when the last message is inbound and the
 * chat has not been opened since that timestamp. A WAHA count greater than 0
 * still wins.
 */
export function displayedWhatsAppInboxUnread(
  chat: WhatsAppInboxChat,
  state: WhatsAppInboxUnreadState,
): number {
  const apiUnread = state.apiUnread[chat.id] ?? chat.unreadCount;
  const lastTimestamp = chat.lastMessage?.timestamp ?? 0;
  const seenAt = state.seenAt[chat.id] ?? 0;
  if (lastTimestamp <= seenAt) {
    return apiUnread;
  }
  const bump = state.bumps[chat.id] ?? 0;
  const seed = chat.lastMessage?.fromMe === false && bump === 0 && apiUnread === 0 ? 1 : 0;
  return Math.max(apiUnread, bump, seed);
}

export function overlayWhatsAppInboxChatUnread(
  placeId: string,
  chat: WhatsAppInboxChat,
): WhatsAppInboxChat {
  const state = readState(placeId);
  state.apiUnread[chat.id] = chat.unreadCount;
  writeState(placeId, state);
  return {
    ...chat,
    unreadCount: displayedWhatsAppInboxUnread(chat, state),
  };
}

export function recordWhatsAppInboxUnreadBump(
  placeId: string,
  chatId: string,
  unreadCount: number,
): void {
  const state = readState(placeId);
  state.bumps[chatId] = unreadCount;
  writeState(placeId, state);
}

export function markWhatsAppInboxChatSeen(
  placeId: string,
  chatId: string,
  timestamp: number,
): void {
  const state = readState(placeId);
  const previous = state.seenAt[chatId] ?? 0;
  state.seenAt[chatId] = Math.max(previous, timestamp);
  const { [chatId]: _cleared, ...bumps } = state.bumps;
  state.bumps = bumps;
  writeState(placeId, state);
}

/** Opening a chat clears the local badge. A count that came from WAHA stays. */
export function acknowledgeWhatsAppInboxChat(
  queryClient: QueryClient,
  placeId: string,
  chat: WhatsAppInboxChat,
): void {
  markWhatsAppInboxChatSeen(placeId, chat.id, chat.lastMessage?.timestamp ?? 0);
  const displayed = displayedWhatsAppInboxUnread(chat, readState(placeId));
  queryClient.setQueryData<WhatsAppInboxChatsCache>(
    notificationQueryKeys.whatsappInboxChats(placeId),
    (current) => {
      if (current === undefined) {
        return current;
      }
      return {
        ...current,
        chats: current.chats.map((row) =>
          row.id === chat.id ? { ...row, unreadCount: displayed } : row,
        ),
      };
    },
  );
}
